import React, { useEffect, useMemo } from "react";
import { Select, Avatar, Space } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useChat } from "./ChatContext";

// Butler only has two user_type values (see Models/UserType.php) — order and
// label them the same way the admin Users pages already do ("Tenant" /
// "Employee"), just spelled out for a people-picker. Anyone we can't resolve
// a user_type for yet (role data still loading) falls into "Other" rather
// than being silently dropped from the list.
const GROUP_ORDER: Record<string, number> = { tenant: 0, employee: 1 };
const GROUP_LABEL: Record<string, string> = { tenant: "Tenant Users", employee: "Employees" };

const UserPicker: React.FC<{
  value?: number[];
  onChange?: (userIds: number[]) => void;
  excludeUserIds?: number[];
  placeholder?: string;
}> = ({ value, onChange, excludeUserIds, placeholder }) => {
  const { realmUsers, refreshRealmUsers, myUserId, roleFor, userTypeFor } = useChat();

  useEffect(() => {
    if (!realmUsers.length) refreshRealmUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const excluded = useMemo(() => new Set([myUserId, ...(excludeUserIds ?? [])]), [myUserId, excludeUserIds]);

  const groupedOptions = useMemo(() => {
    const groups: Record<string, { value: number; label: string; email: string; role?: string }[]> = {};
    realmUsers
      .filter((u) => u.is_active !== false && !excluded.has(u.user_id))
      .forEach((u) => {
        const userType = userTypeFor(u.email) ?? "other";
        (groups[userType] ??= []).push({
          value: u.user_id,
          label: u.full_name,
          email: u.email,
          role: roleFor(u.email),
        });
      });

    return Object.entries(groups)
      .sort(([a], [b]) => (GROUP_ORDER[a] ?? 99) - (GROUP_ORDER[b] ?? 99))
      .map(([userType, opts]) => ({
        label: GROUP_LABEL[userType] ?? "Other",
        title: GROUP_LABEL[userType] ?? "Other",
        options: [...opts].sort((a, b) => a.label.localeCompare(b.label)),
      }));
  }, [realmUsers, excluded, roleFor, userTypeFor]);

  return (
    <Select
      mode="multiple"
      value={value}
      onChange={onChange}
      placeholder={placeholder ?? "Search people…"}
      style={{ width: "100%" }}
      optionFilterProp="label"
      options={groupedOptions}
      optionRender={(option) => {
        // Grouped `options` makes antd infer optionRender's generic from the
        // group shape ({ label, title, options }), not the leaf option it
        // actually passes at render time — cast to reach the leaf fields.
        const data = option.data as unknown as { label: string; email: string; role?: string };
        return (
          <Space>
            <Avatar size="small" icon={<UserOutlined />} />
            <span>{data.label}</span>
            {data.role && <span style={{ color: "rgba(0,0,0,0.45)", fontSize: 12 }}>{data.role}</span>}
            <span style={{ color: "rgba(0,0,0,0.45)", fontSize: 12 }}>{data.email}</span>
          </Space>
        );
      }}
    />
  );
};

export default UserPicker;
